/**
 * What this screen and browser can do, worked out from plain values so it can
 * be tested without a browser: fullscreen support, Home Screen (standalone)
 * mode, platform, and how big the 640×360 game ends up.
 */

export type FullscreenApi = 'standard' | 'webkit' | 'none';
export type Platform = 'ios' | 'android' | 'other';

export interface FullscreenProbe {
  fullscreenEnabled?: boolean;
  webkitFullscreenEnabled?: boolean;
  /** `requestFullscreen` exists on elements. */
  hasRequest: boolean;
  hasWebkitRequest: boolean;
}

/**
 * Which fullscreen API to call. iPhone Safari has none for page elements
 * (only video), so it reports 'none' and the game offers Add to Home Screen
 * instead; iPad Safari has the webkit one.
 */
export function fullscreenApi(p: FullscreenProbe): FullscreenApi {
  if (p.hasRequest && p.fullscreenEnabled !== false) return 'standard';
  if (p.hasWebkitRequest && p.webkitFullscreenEnabled !== false) return 'webkit';
  return 'none';
}

/** iPadOS Safari calls itself a Mac; touch points give it away. */
export function platformOf(userAgent: string, maxTouchPoints: number): Platform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
}

export interface StandaloneProbe {
  /** matchMedia('(display-mode: standalone)') or fullscreen / minimal-ui. */
  displayModeStandalone: boolean;
  /** iOS Safari's own flag for Home Screen apps. */
  navigatorStandalone?: boolean;
}

export function isStandalone(p: StandaloneProbe): boolean {
  return p.displayModeStandalone || p.navigatorStandalone === true;
}

export interface FitRect {
  width: number;
  height: number;
  /** CSS pixels per game pixel. */
  scale: number;
  left: number;
  top: number;
}

/**
 * The biggest the game can be inside a viewport without stretching or
 * cropping (Phaser's FIT does the same), centred, with the leftover as
 * letterbox bars.
 */
export function fitRect(viewW: number, viewH: number, gameW = 640, gameH = 360): FitRect {
  if (viewW <= 0 || viewH <= 0) return { width: 0, height: 0, scale: 0, left: 0, top: 0 };
  const scale = Math.min(viewW / gameW, viewH / gameH);
  const width = gameW * scale;
  const height = gameH * scale;
  return { width, height, scale, left: (viewW - width) / 2, top: (viewH - height) / 2 };
}

/** Share of the screen the game covers (1 = edge to edge). */
export function screenCoverage(viewW: number, viewH: number, gameW = 640, gameH = 360): number {
  const r = fitRect(viewW, viewH, gameW, gameH);
  return viewW > 0 && viewH > 0 ? (r.width * r.height) / (viewW * viewH) : 0;
}

/** A phone held upright: the wide game would use under a third of it. */
export function shouldSuggestRotate(viewW: number, viewH: number, coarsePointer: boolean): boolean {
  return coarsePointer && viewH > viewW;
}

/**
 * The in-game "back to fullscreen" button: on phones and tablets that can go
 * fullscreen, once play has started and fullscreen was left (a swipe, the back
 * gesture, a notification). Never for a Home Screen app, which already fills
 * the screen, and not while a request is still in flight.
 */
export function showFullscreenReentry(o: {
  inGame: boolean;
  touch: boolean;
  api: FullscreenApi;
  standalone: boolean;
  fullscreen: boolean;
  pending: boolean;
  /** The location plate owns the top-left corner for its entire presentation. */
  locationPlateVisible?: boolean;
}): boolean {
  return o.inGame && o.touch && o.api !== 'none' && !o.standalone && !o.fullscreen && !o.pending && !o.locationPlateVisible;
}

export type DisplayOffer = 'fullscreen' | 'ios-home-screen' | null;

/** What the title screen's corner button offers, if anything (fullscreen toggles both ways). */
export function displayOffer(o: { api: FullscreenApi; platform: Platform; standalone: boolean }): DisplayOffer {
  if (o.standalone) return null;
  if (o.api !== 'none') return 'fullscreen';
  return o.platform === 'ios' ? 'ios-home-screen' : null;
}
